import React, { useCallback, useEffect, useRef, useState } from 'react';
import redactorService from '../services/redactorService';
import RedactorDrawOverlay from './RedactorDrawOverlay';

/**
 * Category label map for display purposes.
 */
const CATEGORY_LABELS = {
  first_name: 'First Name',
  middle_name: 'Middle Name',
  last_name: 'Last Name',
  personal_address: 'Address',
  birthday: 'Birthday',
  full_ssn: 'Full SSN',
  partial_ssn: 'Partial SSN',
  full_account_number: 'Account #',
  partial_account_number: 'Partial Account #',
  itin: 'ITIN',
  ptin: 'PTIN',
  ein: 'EIN',
  personal_phone: 'Phone',
  personal_email: 'Email',
  manual: 'Manual',
};

const getMatchHighlightStyle = (match) => {
  const pageWidth = Number(match.page_width);
  const pageHeight = Number(match.page_height);
  const hasPageDimensions = pageWidth > 0 && pageHeight > 0;
  const coordinatesAreNormalized = [match.x, match.y, match.width, match.height]
    .every((value) => Number(value) >= 0 && Number(value) <= 1);

  if (!hasPageDimensions && !coordinatesAreNormalized) return null;

  const xScale = hasPageDimensions ? pageWidth : 1;
  const yScale = hasPageDimensions ? pageHeight : 1;
  return {
    left: `${(match.x / xScale) * 100}%`,
    top: `${(match.y / yScale) * 100}%`,
    width: `${(match.width / xScale) * 100}%`,
    height: `${(match.height / yScale) * 100}%`,
  };
};

/**
 * RedactorResultsViewer – view original vs redacted, match summary, 
 * manual edit controls, and download buttons.
 */
function RedactorResultsViewer({ jobId, files, onRefresh, redactionStatus, redactionError, onPreviewVersionChange }) {
  const defaultPreviewVersion = (!!redactionStatus && redactionStatus !== 'completed' && !redactionError)
    ? 'original'
    : 'redacted';

  const [selectedFileId, setSelectedFileId] = useState(null);
  const [showFilePicker, setShowFilePicker] = useState(false);
  const [showMatchesPanel, setShowMatchesPanel] = useState(false);
  const [previewVersion, setPreviewVersion] = useState(defaultPreviewVersion);
  useEffect(() => {
    onPreviewVersionChange?.(previewVersion);
  }, [previewVersion, onPreviewVersionChange]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [drawMode, setDrawMode] = useState(null); // null | 'add' | 'remove'
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [drawPage, setDrawPage] = useState(1);
  const [pageImageUrl, setPageImageUrl] = useState(null);
  const [loadingPageImage, setLoadingPageImage] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [rotating, setRotating] = useState(false);
  const [txtSelection, setTxtSelection] = useState(null); // { text, x, y } | null
  const [savingTxt, setSavingTxt] = useState(false);
  const [highlightedMatch, setHighlightedMatch] = useState(null); // raw_matched_text of selected sidebar item

  // Cache blob URLs keyed by `${fileId}_${version}` for non-PDF iframe previews
  const urlCacheRef = useRef({});

  // Cache txt content keyed by `${fileId}_${version}` for txt file rendering
  const txtCacheRef = useRef({});

  // Cache page images keyed by `${fileId}_${version}_${page}` for PDF previews
  const pageImageCacheRef = useRef({});
  const [pageImagesReady, setPageImagesReady] = useState(false);
  const [, setPageImageRevision] = useState(0);
  const previewManuallySetRef = useRef(false);

  // Select first file by default
  useEffect(() => {
    if (files?.length > 0 && !selectedFileId) {
      setSelectedFileId(files[0].file_id);
    }
  }, [files, selectedFileId]);

  useEffect(() => {
    previewManuallySetRef.current = false;
  }, [selectedFileId]);

  useEffect(() => {
    if (!previewManuallySetRef.current) {
      setPreviewVersion(defaultPreviewVersion);
    }
  }, [defaultPreviewVersion, selectedFileId]);

  useEffect(() => {
    setShowFilePicker(false);
  }, [selectedFileId]);

  // Clear sidebar highlight when selected file changes
  useEffect(() => {
    setHighlightedMatch(null);
  }, [selectedFileId]);

  // Pre-fetch both versions when a file is selected
  const prefetchPreviews = useCallback(async (fileId) => {
    if (!jobId || !fileId) return;
    const cache = urlCacheRef.current;
    const txtCache = txtCacheRef.current;
    const file = files?.find((f) => f.file_id === fileId);
    const name = file?.original_filename?.toLowerCase() || '';
    const filIsTxt = ['.txt', '.sql', '.psql', '.csv', '.json', '.xml', '.md'].some((ext) => name.endsWith(ext));

    for (const ver of ['original', 'redacted']) {
      const key = `${fileId}_${ver}`;
      if (filIsTxt) {
        // For txt: fetch as raw text for in-component rendering
        if (txtCache[key]) continue;
        try {
          txtCache[key] = await redactorService.getFileTxtContent(jobId, fileId, ver);
        } catch (err) {
          console.error(`Txt content fetch error (${ver})`, err);
        }
      } else {
        if (cache[key]) continue;
        try {
          const url = await redactorService.getFilePreviewUrl(jobId, fileId, ver);
          cache[key] = url;
        } catch (err) {
          console.error(`Preview prefetch error (${ver})`, err);
        }
      }
    }
  }, [jobId, files]);

  useEffect(() => {
    if (!selectedFileId) return;
    // Page-based files (PDF/DOCX) use page images instead of iframe blobs
    const file = files?.find((f) => f.file_id === selectedFileId);
    const name = file?.original_filename?.toLowerCase() || '';
    if (name.endsWith('.pdf') || name.endsWith('.docx')) return;
    let cancelled = false;

    const run = async () => {
      setLoadingPreview(true);
      await prefetchPreviews(selectedFileId);
      if (!cancelled) setLoadingPreview(false);
    };
    run();

    return () => { cancelled = true; };
  }, [selectedFileId, prefetchPreviews, files, refreshKey]);

  // Revoke all cached blob URLs on unmount
  useEffect(() => {
    const cache = urlCacheRef.current;
    const imgCache = pageImageCacheRef.current;
    return () => {
      Object.values(cache).forEach((url) => URL.revokeObjectURL(url));
      urlCacheRef.current = {};
      Object.values(imgCache).forEach((url) => URL.revokeObjectURL(url));
      pageImageCacheRef.current = {};
      txtCacheRef.current = {};
    };
  }, []);

  const selectedFile = files?.find((f) => f.file_id === selectedFileId);
  const isPdf = selectedFile?.original_filename?.toLowerCase().endsWith('.pdf');
  const isDocx = selectedFile?.original_filename?.toLowerCase().endsWith('.docx');
  const _selName = selectedFile?.original_filename?.toLowerCase() || '';
  const isTxt = ['.txt', '.sql', '.psql', '.csv', '.json', '.xml', '.md'].some((ext) => _selName.endsWith(ext));
  const isPageBased = isPdf || isDocx; // file types rendered as stacked page images
  const isRedactionPending = !!redactionStatus && redactionStatus !== 'completed' && !redactionError;
  const isRedactionErrored = !!redactionError;

  // Derive the current preview URL/text from cache (instant, no network)
  const previewUrl = (!isTxt && !isPageBased && selectedFileId)
    ? urlCacheRef.current[`${selectedFileId}_${previewVersion}`] || null
    : null;
  const txtOriginalContent = (isTxt && selectedFileId)
    ? txtCacheRef.current[`${selectedFileId}_original`] || null
    : null;
  const txtRedactedContent = (isTxt && selectedFileId)
    ? txtCacheRef.current[`${selectedFileId}_redacted`] || null
    : null;

  // Fetch only the visible version in small batches to avoid flooding Lambda.
  useEffect(() => {
    if (!selectedFileId || !isPageBased || !selectedFile?.page_count) return;
    let cancelled = false;
    const cache = pageImageCacheRef.current;

    const fetchAll = async () => {
      setPageImagesReady(false);
      const pendingPages = [];
      for (let page = 1; page <= selectedFile.page_count; page++) {
        const key = `${selectedFileId}_${previewVersion}_${page}`;
        if (!cache[key]) pendingPages.push(page);
      }

      const BATCH_SIZE = 4;
      for (let index = 0; index < pendingPages.length && !cancelled; index += BATCH_SIZE) {
        const batch = pendingPages.slice(index, index + BATCH_SIZE);
        await Promise.all(
          batch.map(async (page) => {
            const key = `${selectedFileId}_${previewVersion}_${page}`;
            try {
              const url = await redactorService.getPageImageUrl(
                jobId,
                selectedFileId,
                previewVersion,
                page
              );
              if (!cancelled) cache[key] = url;
            } catch (err) {
              console.error(`Page image error (${previewVersion} p${page})`, err);
            }
          })
        );
        if (!cancelled) {
          setPageImageRevision((revision) => revision + 1);
        }
      }
      if (!cancelled) setPageImagesReady(true);
    };

    fetchAll();
    return () => { cancelled = true; };
  }, [selectedFileId, selectedFile?.page_count, isPageBased, jobId, previewVersion, refreshKey]);

  const handleDownloadSingle = async (fileId, filename) => {
    try {
      await redactorService.downloadFile(jobId, fileId, 'redacted', filename);
    } catch (err) {
      console.error('Download error', err);
    }
  };

  const handleManualAdd = async () => {
    setPreviewVersion('redacted');
    setDrawPage(1);
    setLoadingPageImage(true);
    try {
      const url = await redactorService.getPageImageUrl(jobId, selectedFileId, 'redacted', 1);
      setPageImageUrl(url);
      setDrawMode('add');
    } catch (err) {
      console.error('Failed to load page image for draw mode', err);
    } finally {
      setLoadingPageImage(false);
    }
  };

  const handleManualRemoveArea = async () => {
    setPreviewVersion('redacted');
    setDrawPage(1);
    setLoadingPageImage(true);
    try {
      const url = await redactorService.getPageImageUrl(jobId, selectedFileId, 'redacted', 1);
      setPageImageUrl(url);
      setDrawMode('remove');
    } catch (err) {
      console.error('Failed to load page image for remove-area mode', err);
    } finally {
      setLoadingPageImage(false);
    }
  };

  const handleDrawPageChange = async (newPage) => {
    setDrawPage(newPage);
    setLoadingPageImage(true);
    if (pageImageUrl) URL.revokeObjectURL(pageImageUrl);
    try {
      const url = await redactorService.getPageImageUrl(jobId, selectedFileId, 'redacted', newPage);
      setPageImageUrl(url);
    } catch (err) {
      console.error('Failed to load page image', err);
    } finally {
      setLoadingPageImage(false);
    }
  };

  const handleRemoveRedaction = async (redactionId) => {
    setRemovingId(redactionId);
    try {
      const result = await redactorService.editRedaction(jobId, selectedFileId, {
        file_id: selectedFileId,
        page: 1,
        action: 'remove',
        x: 0, y: 0, width: 0, height: 0,
        redaction_id: redactionId,
      });
      // Invalidate cached page images so the rebuilt PDF is shown
      const imgCache = pageImageCacheRef.current;
      for (const key of Object.keys(imgCache)) {
        if (key.startsWith(`${selectedFileId}_redacted_`)) {
          URL.revokeObjectURL(imgCache[key]);
          delete imgCache[key];
        }
      }
      // Invalidate non-PDF (txt/docx) cached redacted blob URL
      const blobKey = `${selectedFileId}_redacted`;
      if (urlCacheRef.current[blobKey]) {
        URL.revokeObjectURL(urlCacheRef.current[blobKey]);
        delete urlCacheRef.current[blobKey];
      }
      // Invalidate txt content cache
      if (txtCacheRef.current[blobKey]) {
        delete txtCacheRef.current[blobKey];
      }
      // Refresh parent state immediately with the updated job payload
      // and reload images so the next click uses the latest state.
      if (onRefresh) await onRefresh(result?.job || null);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error('Remove redaction error', err);
    } finally {
      setRemovingId(null);
    }
  };

  const handleDrawComplete = async (rect) => {
    const currentMode = drawMode;
    setDrawMode(null);
    if (pageImageUrl) { URL.revokeObjectURL(pageImageUrl); setPageImageUrl(null); }
    if (!selectedFileId) return;
    setSaving(true);
    try {
      const result = await redactorService.editRedaction(jobId, selectedFileId, {
        file_id: selectedFileId,
        page: drawPage,
        action: currentMode === 'remove' ? 'remove_area' : 'add',
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height,
      });
      // Invalidate cached redacted previews
      const cacheKey = `${selectedFileId}_redacted`;
      if (urlCacheRef.current[cacheKey]) {
        URL.revokeObjectURL(urlCacheRef.current[cacheKey]);
        delete urlCacheRef.current[cacheKey];
      }
      // Invalidate redacted page images
      const imgCache = pageImageCacheRef.current;
      for (let p = 1; p <= (selectedFile?.page_count || 0); p++) {
        const pk = `${selectedFileId}_redacted_${p}`;
        if (imgCache[pk]) { URL.revokeObjectURL(imgCache[pk]); delete imgCache[pk]; }
      }
      // Re-fetch non-PDF preview
      try {
        const url = await redactorService.getFilePreviewUrl(jobId, selectedFileId, 'redacted');
        urlCacheRef.current[cacheKey] = url;
      } catch (e) {
        console.error('Re-fetch redacted preview failed', e);
      }
      if (currentMode === 'remove' || result?.job) {
        // Use the updated job payload immediately so the next interaction
        // reflects the latest match list without waiting for a separate reload.
        if (onRefresh) await onRefresh(result?.job || null);
      }
      // Force re-render and trigger page image re-fetch
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error('Draw action error', err);
    } finally {
      setSaving(false);
    }
  };

  const handleTxtAddRedaction = async (phrase) => {
    if (!phrase || !selectedFileId) return;
    setSavingTxt(true);
    setTxtSelection(null);
    try {
      const result = await redactorService.editRedaction(jobId, selectedFileId, {
        file_id: selectedFileId,
        page: 1,
        action: 'add',
        x: 0, y: 0, width: 0, height: 0,
        text_to_redact: phrase,
      });
      if (result.success) {
        // Invalidate txt redacted cache so it re-fetches
        const key = `${selectedFileId}_redacted`;
        if (txtCacheRef.current[key]) delete txtCacheRef.current[key];
        if (onRefresh) await onRefresh(result?.job || null);
        setRefreshKey((k) => k + 1);
      }
    } catch (err) {
      console.error('Txt manual redact error', err);
    } finally {
      setSavingTxt(false);
    }
  };

  const handleTxtUnredact = async (matchingIds) => {
    if (!matchingIds?.length || !selectedFileId) return;
    setSavingTxt(true);
    setTxtSelection(null);
    try {
      for (const redactionId of matchingIds) {
        await redactorService.editRedaction(jobId, selectedFileId, {
          file_id: selectedFileId,
          page: 1,
          action: 'remove',
          x: 0, y: 0, width: 0, height: 0,
          redaction_id: redactionId,
        });
      }
      // Invalidate both versions since original may also change
      for (const ver of ['original', 'redacted']) {
        const key = `${selectedFileId}_${ver}`;
        if (txtCacheRef.current[key]) delete txtCacheRef.current[key];
      }
      if (onRefresh) await onRefresh(null);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error('Txt unredact error', err);
    } finally {
      setSavingTxt(false);
    }
  };

  const handleTxtMouseUp = () => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) { setTxtSelection(null); return; }
    const text = selection.toString().trim();
    if (!text) { setTxtSelection(null); return; }
    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();

    // Check if the selection contains any block (redacted) characters
    const hasBlocks = text.includes('█');

    let matchingIds = [];
    if (hasBlocks) {
      const allMatches = selectedFile?.matches || [];
      const rawFull = (txtCacheRef.current[`${selectedFileId}_${previewVersion}`] || '').replace(/\r\n/g, '\n');

      // Compute approximate start offset via DOM range
      let approxOffset = 0;
      try {
        let preEl = range.startContainer;
        if (preEl.nodeType === Node.TEXT_NODE) preEl = preEl.parentElement;
        while (preEl && preEl.tagName !== 'PRE') preEl = preEl.parentElement;
        if (preEl) {
          const preRange = document.createRange();
          preRange.setStart(preEl, 0);
          preRange.setEnd(range.startContainer, range.startOffset);
          approxOffset = preRange.toString().replace(/\r\n/g, '\n').length;
        }
      } catch (_) { }

      // Find the first █ run at or after the selection start offset in the full text.
      // This is the block the cursor was on when the drag started.
      let targetBlock = null;
      let targetBlockOccurrenceIdx = 0;

      if (rawFull) {
        let scanPos = approxOffset;
        // Scan forward to find the nearest █
        while (scanPos < rawFull.length && rawFull[scanPos] !== '█') scanPos++;
        if (scanPos < rawFull.length) {
          let blockEnd = scanPos;
          while (blockEnd < rawFull.length && rawFull[blockEnd] === '█') blockEnd++;
          const blockLen = blockEnd - scanPos;
          targetBlock = '█'.repeat(blockLen);
          // Count which occurrence of this block this is
          let oIdx = 0;
          let p = 0;
          while ((p = rawFull.indexOf(targetBlock, p)) !== -1) {
            if (p === scanPos) break;
            oIdx++;
            p += targetBlock.length;
          }
          targetBlockOccurrenceIdx = oIdx;
        }
      }

      // Fallback: use first █ run in the selected text if we couldn't get from full text
      if (!targetBlock) {
        const m = text.match(/█+/);
        targetBlock = m ? m[0] : null;
      }

      console.debug('[Redactor unredact]', { approxOffset, targetBlock, targetBlockOccurrenceIdx });

      if (targetBlock) {
        const matchesForLen = allMatches.filter(
          (m) => m.raw_matched_text && m.raw_matched_text.length === targetBlock.length
        );
        const idx = Math.min(targetBlockOccurrenceIdx, matchesForLen.length - 1);
        if (idx >= 0) {
          matchingIds = [matchesForLen[idx].redaction_id];
        }
      }
    } else {
      // Original text: exact phrase match
      matchingIds = (selectedFile?.matches || [])
        .filter((m) => m.raw_matched_text === text)
        .map((m) => m.redaction_id);
    }

    setTxtSelection({ text, x: rect.left + rect.width / 2, y: rect.top, matchingIds, hasBlocks });
  };

  // Dismiss selection tooltip when clicking outside
  useEffect(() => {
    const dismiss = (e) => {
      if (!e.target.closest('[data-txt-tooltip]')) setTxtSelection(null);
    };
    document.addEventListener('mousedown', dismiss);
    return () => document.removeEventListener('mousedown', dismiss);
  }, []);

  const handleRotate = async (degrees) => {
    if (!selectedFileId) return;
    setRotating(true);
    try {
      await redactorService.rotatePdf(jobId, selectedFileId, degrees);
      // Invalidate all cached page images for this file so they re-fetch
      const imgCache = pageImageCacheRef.current;
      for (const key of Object.keys(imgCache)) {
        if (key.startsWith(`${selectedFileId}_`)) {
          URL.revokeObjectURL(imgCache[key]);
          delete imgCache[key];
        }
      }
      // Also invalidate non-PDF blob preview cache
      for (const ver of ['original', 'redacted']) {
        const key = `${selectedFileId}_${ver}`;
        if (urlCacheRef.current[key]) {
          URL.revokeObjectURL(urlCacheRef.current[key]);
          delete urlCacheRef.current[key];
        }
      }
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error('Rotate PDF error', err);
    } finally {
      setRotating(false);
    }
  };

  if (!files || files.length === 0) return null;

  // Splits text content and wraps matching phrase in <mark> for highlight
  const renderTxtWithHighlight = (content, term) => {
    if (!term || !content) return content || '';
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = content.split(new RegExp(`(${escaped})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === term.toLowerCase()
        ? <mark key={i} style={{ backgroundColor: '#ffe066', color: 'inherit', borderRadius: 2 }}>{part}</mark>
        : part
    );
  };

  return (
    <div className="d-flex flex-column h-100">
      {/* Floating redact tooltip for txt text selection */}
      {txtSelection && (
        <div
          data-txt-tooltip="true"
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            left: txtSelection.x,
            top: txtSelection.y - 46,
            transform: 'translateX(-50%)',
            zIndex: 9999,
            background: '#212529',
            color: '#fff',
            borderRadius: 5,
            padding: '4px 6px',
            display: 'flex',
            gap: 4,
            alignItems: 'center',
            boxShadow: '0 2px 10px rgba(0,0,0,0.35)',
            pointerEvents: 'auto',
            userSelect: 'none',
          }}
        >
          {!txtSelection.hasBlocks && (
            <button
              className="btn btn-sm btn-danger py-0 px-2"
              style={{ fontSize: 11 }}
              onClick={() => handleTxtAddRedaction(txtSelection.text)}
              disabled={savingTxt}
            >
              {savingTxt ? (
                <span className="spinner-border spinner-border-sm" />
              ) : (
                <><i className="bi bi-eraser-fill me-1"></i>Redact</>
              )}
            </button>
          )}
          {txtSelection.matchingIds?.length > 0 && (
            <button
              className="btn btn-sm btn-success py-0 px-2"
              style={{ fontSize: 11 }}
              onClick={() => handleTxtUnredact(txtSelection.matchingIds)}
              disabled={savingTxt}
              title={`Remove ${txtSelection.matchingIds.length} redaction(s) for this text`}
            >
              {savingTxt ? (
                <span className="spinner-border spinner-border-sm" />
              ) : (
                <><i className="bi bi-eye me-1"></i>Unredact{txtSelection.matchingIds.length > 1 ? ` (${txtSelection.matchingIds.length})` : ''}</>
              )}
            </button>
          )}
          <button
            className="btn btn-sm btn-outline-light py-0 px-1"
            style={{ fontSize: 11 }}
            onClick={() => setTxtSelection(null)}
          >
            <i className="bi bi-x"></i>
          </button>
        </div>
      )}
      <div className="row flex-grow-1" style={{ minHeight: 0 }}>
        {/* Main content area */}
        <div className="col-12 d-flex flex-column h-100">
          {selectedFile && (
            <>
              <div className="d-flex justify-content-between align-items-start mb-2 gap-2" style={{ position: 'relative' }}>
                <div className="position-relative flex-shrink-0" style={{ zIndex: 40 }}>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm d-flex align-items-center gap-2"
                    onClick={() => setShowFilePicker((prev) => !prev)}
                    style={{ minWidth: 170, maxWidth: 210 }}
                  >
                    <span className="text-truncate" style={{ maxWidth: 110 }}>
                      {selectedFile.original_filename}
                    </span>
                    <span className="badge bg-light text-primary rounded-pill">{selectedFile.matches?.length || 0}</span>
                    <i className={`bi ${showFilePicker ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                  </button>
                  {showFilePicker && (
                    <div
                      className="position-absolute mt-1 bg-white border rounded shadow"
                      style={{
                        width: 'min(560px, calc(100vw - 40px))',
                        maxHeight: 'calc(100vh - 180px)',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        zIndex: 2000,
                      }}
                    >
                      <div className="list-group list-group-flush">
                        {files.map((f) => {
                          const isSelected = selectedFileId === f.file_id;
                          return (
                            <div className="border rounded mb-3 bg-white flex-shrink-0" style={{ maxHeight: 260, overflowY: 'auto' }}>
                              {Object.keys(selectedFile.match_summary || {}).length === 0 ? (
                                <div className="px-3 py-2 text-muted small">No matches found.</div>
                              ) : (
                                <table className="table table-sm mb-0" style={{ fontSize: 12 }}>
                                  <tbody>
                                    {Object.entries(selectedFile.match_summary).map(([cat, count]) => {
                                      const catKey = `${selectedFile.file_id}_${cat}`;
                                      const isCatOpen = expandedCategories.has(catKey);
                                      const catMatches = (selectedFile.matches || []).filter((m) => m.category === cat);
                                      return (
                                        <React.Fragment key={cat}>
                                          <tr
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => {
                                              setExpandedCategories((prev) => {
                                                const next = new Set(prev);
                                                if (next.has(catKey)) next.delete(catKey);
                                                else next.add(catKey);
                                                return next;
                                              });
                                            }}
                                          >
                                            <td className="ps-3">
                                              <i className={`bi ${isCatOpen ? 'bi-chevron-down' : 'bi-chevron-right'} me-1 text-muted`} style={{ fontSize: 10 }}></i>
                                              <i className="bi bi-shield-fill-check text-success me-1"></i>
                                              {CATEGORY_LABELS[cat] || cat}
                                            </td>
                                            <td className="text-end pe-3 text-muted">{count}</td>
                                          </tr>
                                          {isCatOpen && catMatches.map((m) => (
                                            <tr
                                              key={m.redaction_id}
                                              style={{
                                                cursor: 'pointer',
                                                backgroundColor: highlightedMatch === (m.raw_matched_text || m.matched_text) ? '#fff3cd' : '',
                                              }}
                                              onClick={() => {
                                                const text = m.raw_matched_text || m.matched_text;
                                                setHighlightedMatch((prev) => (prev === text ? null : text));
                                              }}
                                              title="Click to highlight in document"
                                            >
                                              <td className="ps-4" colSpan={2}>
                                                <div className="d-flex align-items-center justify-content-between">
                                                  <span>
                                                    <code className="text-danger" style={{ fontSize: 11 }}>{m.raw_matched_text || m.matched_text}</code>
                                                    <span className="text-muted ms-1" style={{ fontSize: 10 }}>p.{m.page}</span>
                                                  </span>
                                                  <button
                                                    className="btn btn-link btn-sm p-0 text-danger"
                                                    title="Cancel this redaction"
                                                    disabled={removingId !== null}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleRemoveRedaction(m.redaction_id);
                                                    }}
                                                  >
                                                    {removingId === m.redaction_id ? (
                                                      <span className="spinner-border spinner-border-sm" />
                                                    ) : (
                                                      <i className="bi bi-x-circle" style={{ fontSize: 12 }}></i>
                                                    )}
                                                  </button>
                                                </div>
                                              </td>
                                            </tr>
                                          ))}
                                        </React.Fragment>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end ms-auto">
                  <div className="btn-group btn-group-sm flex-shrink-0">
                    <button
                      className={`btn ${previewVersion === 'original' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => {
                        previewManuallySetRef.current = true;
                        setPreviewVersion('original');
                      }}
                    >
                      Original
                    </button>
                    <button
                      className={`btn ${previewVersion === 'redacted' ? 'btn-primary' : 'btn-outline-primary'} position-relative`}
                      onClick={() => {
                        previewManuallySetRef.current = true;
                        setPreviewVersion('redacted');
                      }}
                      title={isRedactionErrored ? redactionError : (isRedactionPending ? 'Redaction pending' : undefined)}
                    >
                      <span className="position-relative d-inline-flex align-items-center">
                        Redacted
                        {isRedactionPending && (
                          <span
                            className="position-absolute top-0 start-100 translate-middle rounded-circle bg-white border border-primary d-inline-flex align-items-center justify-content-center"
                            style={{ width: 16, height: 16 }}
                          >
                            <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true" style={{ width: 10, height: 10 }}></span>
                            <span className="visually-hidden">Redaction pending</span>
                          </span>
                        )}
                        {isRedactionErrored && (
                          <span
                            className="position-absolute top-0 start-100 translate-middle rounded-circle bg-danger text-white d-inline-flex align-items-center justify-content-center"
                            style={{ width: 16, height: 16, fontSize: 11, lineHeight: 1 }}
                          >
                            !
                            <span className="visually-hidden">Redaction error</span>
                          </span>
                        )}
                      </span>
                    </button>
                  </div>
                  {isPdf && (
                    <>
                      <button
                        className={`btn btn-sm ${drawMode === 'add' ? 'btn-dark' : 'btn-outline-dark'}`}
                        onClick={() => drawMode ? setDrawMode(null) : handleManualAdd()}
                        title={drawMode ? 'Cancel drawing' : 'Manually redact an area'}
                        disabled={saving || loadingPageImage}
                      >
                        {saving && drawMode !== 'remove' ? (
                          <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</>
                        ) : loadingPageImage && drawMode !== 'remove' ? (
                          <><span className="spinner-border spinner-border-sm me-1"></span>Loading...</>
                        ) : drawMode === 'add' ? (
                          <><i className="bi bi-x-lg me-1"></i>Cancel Draw</>
                        ) : (
                          <><i className="bi bi-pencil-square me-1"></i><span className="text-nowrap">Redact Area</span></>
                        )}
                      </button>
                      <button
                        className={`btn btn-sm ${drawMode === 'remove' ? 'btn-success' : 'btn-outline-success'}`}
                        onClick={() => drawMode ? setDrawMode(null) : handleManualRemoveArea()}
                        title={drawMode ? 'Cancel drawing' : 'Draw a box to unredact that area'}
                        disabled={saving || loadingPageImage}
                      >
                        {saving && drawMode === 'remove' ? (
                          <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</>
                        ) : loadingPageImage && drawMode === 'remove' ? (
                          <><span className="spinner-border spinner-border-sm me-1"></span>Loading...</>
                        ) : drawMode === 'remove' ? (
                          <><i className="bi bi-x-lg me-1"></i>Cancel Draw</>
                        ) : (
                          <><i className="bi bi-eraser me-1"></i><span className="text-nowrap">Unredact Area</span></>
                        )}
                      </button>
                    </>
                  )}
                  <button
                    className="btn btn-sm btn-outline-success"
                    onClick={() =>
                      handleDownloadSingle(
                        selectedFile.file_id,
                        selectedFile.original_filename
                      )
                    }
                  >
                    <i className="bi bi-download me-1"></i><span className="text-nowrap">Download</span>
                  </button>
                  {isPdf && (
                    <>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => handleRotate(90)}
                        disabled={rotating || saving}
                        title="Rotate clockwise 90°"
                      >
                        {rotating ? (
                          <span className="spinner-border spinner-border-sm" />
                        ) : (
                          <i className="bi bi-arrow-clockwise"></i>
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>
              {/* Preview – both versions rendered; toggle visibility for instant switching */}
              <div
                className="border rounded"
                style={{ background: '#f8f9fa', position: 'relative', flex: '1', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
              >
                {drawMode && pageImageUrl ? (
                  /* Draw mode: render page as image so overlay coordinates map exactly */
                  <div style={{ position: 'relative', flex: '1', minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                    <img
                      src={pageImageUrl}
                      alt={`Page ${drawPage}`}
                      style={{ width: '100%', display: 'block' }}
                      draggable={false}
                    />
                    <RedactorDrawOverlay
                      active={!!drawMode}
                      mode={drawMode}
                      onDraw={handleDrawComplete}
                      onCancel={() => { setDrawMode(null); if (pageImageUrl) { URL.revokeObjectURL(pageImageUrl); setPageImageUrl(null); } }}
                    />
                    {/* Page navigation controls */}
                    {selectedFile.page_count > 1 && (
                      <div className="d-flex justify-content-center align-items-center gap-2 py-2 bg-white border-top">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          disabled={drawPage <= 1 || loadingPageImage}
                          onClick={() => handleDrawPageChange(drawPage - 1)}
                        >
                          <i className="bi bi-chevron-left"></i>
                        </button>
                        <span className="small">
                          Page {drawPage} of {selectedFile.page_count}
                        </span>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          disabled={drawPage >= selectedFile.page_count || loadingPageImage}
                          onClick={() => handleDrawPageChange(drawPage + 1)}
                        >
                          <i className="bi bi-chevron-right"></i>
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Normal mode: page images for PDFs/DOCX (scroll-synced), iframe for others */
                  <div style={{ flex: '1', minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                    {isPageBased ? (
                      <>
                        {!pageImagesReady && (
                          <div className="d-flex justify-content-center align-items-center gap-2 py-3 bg-white border-bottom">
                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                              <span className="visually-hidden">Loading pages...</span>
                            </div>
                            <span className="small text-muted">Loading {previewVersion} pages...</span>
                          </div>
                        )}
                        {Array.from({ length: selectedFile.page_count }, (_, i) => i + 1).map((pageNum) => {
                          const pageUrl = pageImageCacheRef.current[`${selectedFileId}_${previewVersion}_${pageNum}`];
                          if (!pageUrl) return null;
                          const pageHighlights = highlightedMatch
                            ? (selectedFile?.matches || []).filter(
                              (m) => (m.raw_matched_text || m.matched_text) === highlightedMatch && m.page === pageNum
                            )
                            : [];
                          return (
                            <div key={pageNum} style={{ position: 'relative', lineHeight: 0, overflow: 'hidden' }}>
                              <img
                                src={pageUrl}
                                alt={`${previewVersion === 'original' ? 'Original' : 'Redacted'} page ${pageNum}`}
                                style={{ width: '100%', display: 'block' }}
                                draggable={false}
                              />
                              {pageHighlights.map((m) => {
                                const highlightStyle = getMatchHighlightStyle(m);
                                if (!highlightStyle) return null;
                                return (
                                  <div
                                    key={m.redaction_id}
                                    style={{
                                      position: 'absolute',
                                      ...highlightStyle,
                                      backgroundColor: 'rgba(255, 230, 0, 0.4)',
                                      border: '2px solid #f0c000',
                                      pointerEvents: 'none',
                                      zIndex: 10,
                                    }}
                                  />
                                );
                              })}
                            </div>
                          );
                        })}
                      </>
                    ) : (
                      <>
                        {loadingPreview ? (
                          <div className="d-flex justify-content-center align-items-center" style={{ height: '100%' }}>
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading preview...</span>
                            </div>
                          </div>
                        ) : isTxt ? (
                          <div
                            key={`${selectedFileId}_${refreshKey}`}
                            style={{ position: 'relative' }}
                          >
                            {/* Original – sets natural scroll height */}
                            <pre
                              onMouseUp={previewVersion === 'original' ? handleTxtMouseUp : undefined}
                              style={{
                                margin: 0,
                                padding: '1rem',
                                background: '#ffffff',
                                color: '#212529',
                                fontFamily: 'monospace',
                                fontSize: 13,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                visibility: previewVersion === 'original' ? 'visible' : 'hidden',
                                pointerEvents: previewVersion === 'original' ? 'auto' : 'none',
                              }}
                            >
                              {highlightedMatch ? renderTxtWithHighlight(txtOriginalContent || '', highlightedMatch) : (txtOriginalContent || '')}
                            </pre>
                            {/* Redacted – absolute overlay, shares scroll container */}
                            <pre
                              onMouseUp={previewVersion === 'redacted' ? handleTxtMouseUp : undefined}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                margin: 0,
                                padding: '1rem',
                                background: '#ffffff',
                                color: '#212529',
                                fontFamily: 'monospace',
                                fontSize: 13,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                visibility: previewVersion === 'redacted' ? 'visible' : 'hidden',
                                pointerEvents: previewVersion === 'redacted' ? 'auto' : 'none',
                              }}
                            >
                              {highlightedMatch ? renderTxtWithHighlight(txtRedactedContent || '', highlightedMatch) : (txtRedactedContent || '')}
                            </pre>
                          </div>
                        ) : previewUrl ? (
                          <iframe
                            key={`${selectedFileId}_${previewVersion}_${refreshKey}`}
                            src={previewUrl}
                            title={`${previewVersion} preview`}
                            style={{
                              width: '100%',
                              height: '100%',
                              border: 'none',
                            }}
                          />
                        ) : (
                          <div className="d-flex justify-content-center align-items-center text-muted" style={{ height: '100%' }}>
                            Preview not available
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default RedactorResultsViewer;
